import { useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import Reaptcha from 'reaptcha';

import Button from '@klv/atoms/button';
import { useTranslation } from '@klv/hooks/useTranslation';
import DynamicTextFormField from '@klv/molecules/dynamic-text-form-field';
import { ScrollToError } from '@klv/organisms/form/utils';
import { submitForm } from '../utils/submitForm';
import TextInput from '@klv/molecules/form-field/text';
import { handleWebsiteBlur } from '@klv/organisms/form-account-access/utils/handleWebsiteBlur';
import TextareaInput from '@klv/molecules/form-field/textarea';
import { initialValues, initialValuesMfa } from '@klv/organisms/form-account-access/fields/zendeskFields';
import { useValidationSchemaYupObjectT } from '@klv/organisms/form-account-access/fields/supportFormFields';
import { MfaSupportFieldsContainer } from '@klv/organisms/form-account-access/components/mfaSupportFieldsContainer';
import { ZendeskSelectInput } from '@klv/organisms/form-account-access/components/zendeskSelectInput';
import { MfaSupportSelectReason } from '@klv/organisms/form-account-access/components/MfaSupportSelectReason';
import useLocation from '@klv/hooks/useLocation';

import * as s from '../styles';

import type { IInitialAccountAccessFormValues, ISupportForm, TAccountAccessFormValues } from '../types';

const RequestForm = ({
  backgroundColor = 'light',
  setSuccess,
}: ISupportForm) => {
  const location = useLocation();
  const { t } = useTranslation();
  const validationSchemaYupObject = useValidationSchemaYupObjectT();
  
  const [isMfaIssue, setIsMfaIssue] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const errorElement = useRef<HTMLDivElement>(null);
  const recaptchaInstance = useRef<Reaptcha>(null);

  const onChange = (e: any) => setIsMfaIssue(e === 'multifactorAuthentication');

  // Create a reset function
  const resetRecaptcha = () => {
    if (recaptchaInstance.current) {
      recaptchaInstance.current.reset();
    }
  };

  // filter values based on issue type selected
  const getFilteredFormValues = (values: TAccountAccessFormValues) => {
    return Object.fromEntries(Object.entries(values).filter(([key]) => {
      return !initialValuesMfa.hasOwnProperty(key) || isMfaIssue && initialValuesMfa.hasOwnProperty(key);
    })) as TAccountAccessFormValues | IInitialAccountAccessFormValues;
  };

  return (
    <>
      {errors?.length > 0 && (
        <s.ErrorContainer>
          {errors.map((error: string) => (
            <ScrollToError key={error} innerRef={errorElement}>
              <s.FormError
                $backgroundColor={backgroundColor}
                variant='formRegular'
              >
                {error}
              </s.FormError>
            </ScrollToError>
          ))}
        </s.ErrorContainer>
      )}

      <Formik
        initialValues={{ ...initialValues, ...initialValuesMfa }}
        validationSchema={validationSchemaYupObject}
        onSubmit={(values: TAccountAccessFormValues) => {
          const filteredValues = getFilteredFormValues(values);
          submitForm(filteredValues, setErrors, resetRecaptcha, setSuccess);
        }}
      >
        {(formik) => {
          const { recipients } = formik.values;
          const { isSubmitting, isValid, dirty: isDirty } = formik;

          const gRecaptchaResponse = Boolean(formik.values['g-recaptcha-response']);
          const hasRecaptchaBypass = new URLSearchParams(location.search).get('token') !== null;
          const hasValidFields =  isValid && (gRecaptchaResponse || hasRecaptchaBypass);
          const isDisabled = !isDirty || !hasValidFields || isSubmitting;

          return (
            <Form>
              <ZendeskSelectInput
                required
                name='blockerType'
                label={t('organisms_forms_accountAccessForm_form_issue_label')}
                fieldName='blockerType'
                backgroundColor={backgroundColor}
                handleChange={onChange}
              />
              {isMfaIssue && (
                <>
                  <MfaSupportSelectReason backgroundColor={'light'} />
                  <s.MfaSupportFieldsInfoBox>
                    {t('organisms_forms_accountAccessForm_form_mfa_support_info')}
                  </s.MfaSupportFieldsInfoBox>
                </>
              )}
              <TextInput
                label={t('organisms_forms_accountAccessForm_form_name_label')}
                name='name'
                placeholder={t(
                  'organisms_forms_accountAccessForm_form_name_placeholder',
                )}
                backgroundColor={backgroundColor}
              />
              <TextInput
                label={t(
                  'organisms_forms_accountAccessForm_form_account_label',
                )}
                name='accountName'
                placeholder={t(
                  'organisms_forms_accountAccessForm_form_account_placeholder',
                )}
                backgroundColor={backgroundColor}
              />
              <TextInput
                label={t('organisms_forms_accountAccessForm_form_email_label')}
                name='email'
                type='email'
                placeholder={t(
                  'organisms_forms_accountAccessForm_form_email_placeholder',
                )}
                backgroundColor={backgroundColor}
              />
              <DynamicTextFormField
                name='recipients'
                maxFields={10}
                label={t(
                  'organisms_forms_accountAccessForm_form_addRecipients_label',
                )}
                placeholder={t(
                  'organisms_forms_accountAccessForm_form_addRecipients_placeholder',
                )}
                type='email'
                addButtonText={t(
                  'organisms_forms_accountAccessForm_form_addRecipients_button_label',
                )}
                values={recipients}
                backgroundColor={backgroundColor}
              />
              <TextInput
                label={t(
                  'organisms_forms_accountAccessForm_form_websiteUrl_label',
                )}
                name='websiteUrl'
                placeholder={t(
                  'organisms_forms_accountAccessForm_form_websiteUrl_placeholder',
                )}
                backgroundColor={backgroundColor}
                onBlur={(event: React.FocusEvent) =>
                  handleWebsiteBlur(event, formik)
                }
              />

              {isMfaIssue ? (
                <MfaSupportFieldsContainer backgroundColor={backgroundColor} />
              ) :
                <TextareaInput
                  label={t('organisms_forms_accountAccessForm_form_help_label')}
                  name='issueDescription'
                  placeholder={t(
                    'organisms_forms_accountAccessForm_form_help_placeholder',
                  )}
                  backgroundColor={backgroundColor}
                  onBlur={(event: React.FocusEvent) =>
                    handleWebsiteBlur(event, formik)
                  }
                />
              }
              <s.RecaptchaContainer>
                <Reaptcha
                  hl={t('langCodes_recaptchaLanguage')}
                  ref={recaptchaInstance}
                  sitekey={process.env.GATSBY_RECAPTCHA_KEY}
                  onVerify={(response) => {
                    formik.setFieldValue('g-recaptcha-response', response);
                  }}
                  onExpire={() => {
                    formik.setFieldValue('g-recaptcha-response', '');
                  }}
                  theme='light'
                />
              </s.RecaptchaContainer>
              <Button
                text={t(
                  'organisms_forms_accountAccessForm_form_submit_button_label',
                )}
                variant='primary'
                type='submit'
                isDisabled={isDisabled}
                disabled={isDisabled}
                data-testid="account-access-form-submit"
              />
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default RequestForm;
